/**
 * Minimap geometry — pure, DOM-free helpers for laying out the minimap box and
 * mapping world coordinates into it. Kept separate from the renderer so the
 * world->minimap mapping is unit-testable without a canvas. The actual drawing
 * lives in the Canvas2D renderer (a read-only second pass over world state).
 */
import type { MinimapCorner } from '../config/config';

export interface MinimapBox {
  /** Top-left of the minimap box, in CSS px. */
  x: number;
  y: number;
  /** Box dimensions in CSS px (aspect-matched to the arena). */
  w: number;
  h: number;
}

/**
 * Compute the minimap box for a given viewport, corner, requested size, margin,
 * and arena dimensions. The box is fit to the arena's aspect ratio: the longer
 * arena axis maps to `sizePx`, the shorter axis is scaled down to match.
 */
export function computeMinimapBox(
  cssW: number,
  cssH: number,
  corner: MinimapCorner,
  sizePx: number,
  margin: number,
  arenaW: number,
  arenaH: number,
): MinimapBox {
  const aspect = arenaW / arenaH;
  let w = sizePx;
  let h = sizePx;
  if (aspect >= 1) h = sizePx / aspect;
  else w = sizePx * aspect;

  const onLeft = corner === 'top-left' || corner === 'bottom-left';
  const onTop = corner === 'top-left' || corner === 'top-right';
  const x = onLeft ? margin : cssW - margin - w;
  const y = onTop ? margin : cssH - margin - h;
  return { x, y, w, h };
}

/** Map a world-space point into minimap (CSS px) space within `box`. */
export function worldToMinimap(
  wx: number,
  wy: number,
  box: MinimapBox,
  arenaW: number,
  arenaH: number,
): { x: number; y: number } {
  return {
    x: box.x + (wx / arenaW) * box.w,
    y: box.y + (wy / arenaH) * box.h,
  };
}
